import { useEffect, useState } from "react";
import clsx from "clsx";
import { premiumApi } from "../api/premium";
import { useGameStore } from "../store/useGameStore";
import type { PremiumStatus } from "../types";
import { FormattedNumber } from "../components/FormattedNumber";

export function PremiumScreen() {
  const { balance, refreshBalance, refreshHero, setScreen, pushInfoMessages } = useGameStore();
  const [status, setStatus] = useState<PremiumStatus | null>(null);
  const [alchemy, setAlchemy] = useState<{
    packs: { diamonds: number; gold: number }[];
    min_vip_level: number;
    vip_level: number;
    subscription_active: boolean;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const [s, a] = await Promise.all([premiumApi.getStatus(), premiumApi.getAlchemyShop()]);
      setStatus(s);
      setAlchemy(a);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const run = async (key: string, fn: () => Promise<{ message?: string }>) => {
    setBusy(key);
    setMessage(null);
    try {
      const res = await fn();
      setMessage(res.message ?? "Done");
      if (res.message) pushInfoMessages([res.message]);
      await load();
      await refreshBalance();
      await refreshHero();
    } catch (e) {
      setMessage(e instanceof Error ? e.message : "Request failed");
    } finally {
      setBusy(null);
    }
  };

  if (loading || !status) {
    return <div className="p-6 text-center text-gray-400">Loading VIP & Pro…</div>;
  }

  const { vip, pro } = status;
  const diamonds = balance?.diamonds ?? 0;
  const energyCapWhileSubscribed = vip.subscription_active
    ? vip.max_energy
    : (vip.subscription_max_energy ?? 400);

  return (
    <div className="p-4 space-y-4 pb-24">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-xl font-bold font-game text-gold-400">VIP & Pro</h2>
        <button type="button" onClick={() => setScreen("main")} className="text-sm text-gray-400">
          Back
        </button>
      </div>

      {message && (
        <div className="rounded-xl border border-kingdom-light/40 bg-kingdom-dark/60 p-3 text-sm text-gray-200">
          {message}
        </div>
      )}

      <section className="rounded-xl border border-amber-500/30 bg-kingdom-purple p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-amber-300">VIP · Lv.{vip.level}</h3>
          <span className="text-xs text-gray-400">{vip.exp.toLocaleString("en-US")} VIP EXP</span>
        </div>
        <p className="text-xs text-gray-400">
          {vip.subscription_active
            ? `Active until ${vip.subscription_expires_at ? new Date(vip.subscription_expires_at).toLocaleDateString() : "—"}`
            : "No active subscription — bonuses paused; VIP level decays over time."}
        </p>
        <ul className="text-xs text-gray-300 space-y-1">
          <li>
            +{vip.subscription_active ? vip.xp_bonus_pct : vip.tier_xp_bonus_pct}% XP while subscribed
            {!vip.subscription_active && vip.tier_xp_bonus_pct > 0 && (
              <span className="text-gray-500"> — subscribe to activate</span>
            )}
          </li>
          <li>
            Max energy {energyCapWhileSubscribed} while subscribed
            {!vip.subscription_active && (
              <span className="text-gray-500"> — subscribe to activate</span>
            )}
          </li>
          {vip.free_arena_skip && !vip.subscription_active && (
            <li>Free solo arena cooldown skip (diamonds)</li>
          )}
          {vip.free_catacombs_skip && <li>Free catacombs auto-clear skip</li>}
          {vip.gem_auto_craft_unlock && <li>Gem auto-craft unlocked</li>}
          {vip.astral_auto_gather_unlock && <li>Auto astral gather (gold budget)</li>}
          {vip.subscription_active && (
            <li>No wait between solo arena fights</li>
          )}
          {vip.subscription_active && (
            <li>Auto HP restore between battles (Blood Packs not consumed; 1🪙 in shop)</li>
          )}
        </ul>
        <button
          type="button"
          disabled={busy !== null || diamonds < status.subscription_cost_diamonds}
          onClick={() => run("vip", () => premiumApi.subscribeVip())}
          className="w-full py-3 rounded-xl bg-amber-500 text-black font-bold disabled:opacity-40"
        >
          {busy === "vip" ? "…" : `Extend VIP ${status.subscription_days}d · 💎 ${status.subscription_cost_diamonds}`}
        </button>
        {vip.weekly_chest_available && (
          <button
            type="button"
            disabled={busy !== null}
            onClick={() => run("chest", () => premiumApi.claimWeeklyChest())}
            className="w-full py-2.5 rounded-xl border border-amber-400 text-amber-200 font-bold text-sm"
          >
            Claim weekly VIP chest
          </button>
        )}
        <p className="text-[11px] text-gray-500 italic">Exclusive mount — coming soon.</p>
      </section>

      <section className="rounded-xl border border-violet-500/30 bg-kingdom-purple p-4 space-y-3">
        <h3 className="font-bold text-violet-200">Pro covenant · Lv.{pro.level}</h3>
        <p className="text-xs text-gray-400">
          {pro.contract_active
            ? `Active · +${pro.stat_bonus_pct}% combat stats (capped)`
            : "Activate Pro to unlock Elite AI and stat bonuses."}
        </p>
        {pro.contract_active && (
          <ul className="text-xs text-gray-300 space-y-1">
            <li>Auto HP restore between battles (Blood Packs not consumed)</li>
            <li>Auto astral gather (gold budget)</li>
          </ul>
        )}
        <button
          type="button"
          disabled={busy !== null || diamonds < status.pro_contract_cost_diamonds}
          onClick={() => run("pro", () => premiumApi.activatePro())}
          className="w-full py-3 rounded-xl bg-violet-600 text-white font-bold disabled:opacity-40"
        >
          {busy === "pro"
            ? "…"
            : `Activate Pro ${status.pro_contract_days}d · 💎 ${status.pro_contract_cost_diamonds}`}
        </button>
      </section>

      {alchemy && (
        <section className="rounded-xl border border-kingdom-light/30 bg-kingdom-dark/50 p-4 space-y-2">
          <h3 className="font-bold text-white text-sm">Alchemy (VIP Lv.{alchemy.min_vip_level}+)</h3>
          {!alchemy.subscription_active || alchemy.vip_level < alchemy.min_vip_level ? (
            <p className="text-xs text-gray-500">Requires active VIP at level {alchemy.min_vip_level}+.</p>
          ) : (
            alchemy.packs.map((pack, i) => (
              <button
                key={i}
                type="button"
                disabled={busy !== null || diamonds < pack.diamonds}
                onClick={() => run(`alchemy-${i}`, () => premiumApi.buyAlchemyPack(i))}
                className={clsx(
                  "w-full py-2.5 rounded-lg border text-sm font-bold",
                  "border-kingdom-glow/50 text-kingdom-glow",
                )}
              >
                💎 {pack.diamonds} → <FormattedNumber value={pack.gold} /> gold
              </button>
            ))
          )}
        </section>
      )}
    </div>
  );
}
